import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

export const THEME_OPTIONS = ['forest', 'mint', 'graphite'];
const THEME_STORAGE_KEY = 'theme';
const DEFAULT_THEME = THEME_OPTIONS[0];

function normalizeTheme(theme) {
  return THEME_OPTIONS.includes(theme) ? theme : DEFAULT_THEME;
}

const ThemeContext = createContext(null);

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(() => {
    if (typeof window === 'undefined') return DEFAULT_THEME;
    try {
      return normalizeTheme(window.localStorage.getItem(THEME_STORAGE_KEY));
    } catch {
      return DEFAULT_THEME;
    }
  });

  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.dataset.theme = theme;
      document.documentElement.style.colorScheme = theme === 'graphite' ? 'dark' : 'light';
    }
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, theme);
    } catch {
      // Ignore storage failures and keep rendering.
    }
  }, [theme]);

  const value = useMemo(() => ({
    theme,
    themeOptions: THEME_OPTIONS,
    setTheme: nextTheme => setTheme(normalizeTheme(nextTheme)),
  }), [theme]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  return useContext(ThemeContext);
}
